import {
  KITS,
  MATCH_FORMAT,
  TEAM_IDENTITY,
  TEAM_INSTRUCTIONS,
  setMatchFormat,
  setMatchMode,
  shortNameFor,
  type TeamIdentity,
  type TeamInstructions,
  type TeamSide,
} from "@golazo/shared";
import type { CareerMatchSetup } from "../career/matchSetup.js";
import type { MatchOptions } from "./match.js";

const SIDES: readonly TeamSide[] = ["home", "away"];

/**
 * Monta un partido de la carrera sobre el estado vivo del juego y devuelve
 * con que deshacerlo.
 *
 * El juego tiene tres objetos globales que un partido lee: la identidad de los
 * equipos (nombre y camiseta), las instrucciones de la IA y el formato. Un
 * partido de carrera los pisa los tres —juegan dos clubes con su estilo, y se
 * puede empatar— y al volver al menu tienen que estar como los dejaste para
 * tu Partido rapido. Por eso se guarda una copia antes de tocar nada.
 *
 * Nada de esto llega al online: el online resetea instrucciones y formato al
 * empezar, y la identidad es de cada pantalla.
 */
export function applyCareerMatch(setup: CareerMatchSetup): () => void {
  const identity = Object.fromEntries(SIDES.map((s) => [s, { ...TEAM_IDENTITY[s] }])) as Record<
    TeamSide,
    TeamIdentity
  >;
  const instructions = Object.fromEntries(SIDES.map((s) => [s, { ...TEAM_INSTRUCTIONS[s] }])) as Record<
    TeamSide,
    TeamInstructions
  >;
  const format = { ...MATCH_FORMAT };

  setMatchMode(setup.modeId);
  for (const side of SIDES) {
    const team = setup.teams[side];
    // Directo y sin recortar: `setTeamIdentity` corta a 16 letras porque es
    // para lo que escribe la gente, y "Polideportivo Municipal" es un club.
    TEAM_IDENTITY[side].name = team.name;
    TEAM_IDENTITY[side].short = shortNameFor(team.name);
    TEAM_IDENTITY[side].kit = KITS[team.kitId] ?? TEAM_IDENTITY[side].kit;
    Object.assign(TEAM_INSTRUCTIONS[side], setup.instructions[side]);
  }
  // Liga: el empate vale un punto. Los minutos, los que tengas puestos.
  setMatchFormat({ onDraw: "draw" });

  return () => {
    for (const side of SIDES) {
      Object.assign(TEAM_IDENTITY[side], identity[side]);
      Object.assign(TEAM_INSTRUCTIONS[side], instructions[side]);
    }
    setMatchFormat(format);
  };
}

/** Las opciones del partido para `createMatch`. */
export function careerMatchOptions(setup: CareerMatchSetup): MatchOptions {
  return {
    seed: setup.seed,
    assist: "normal",
    shirtNumber: setup.shirtNumber,
    nickname: setup.nickname,
    humanSide: setup.side,
    humanSeatIndex: setup.seatIndex,
    talent: setup.talent,
    aiSkill: setup.aiSkill,
    pitchSkin: setup.pitchSkin,
    stadium: setup.stadium,
  };
}
